import { Badge, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import { INITIAL_SUPPLEMENTS, SUPPLEMENT_PROTOCOLS } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint

export default function SupplementsTab() {
  const active = INITIAL_SUPPLEMENTS.filter((s) => s.status === 'active')
  const paused = INITIAL_SUPPLEMENTS.filter((s) => s.status === 'paused')

  const supplementNameById = (id: string): string => {
    const found = INITIAL_SUPPLEMENTS.find((s) => s.id === id)
    return found ? found.name : id
  }

  return (
    <div className="space-y-6">
      {/* ── Summary stats ──────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-3">
        <GradientSphereMetricCard
          label="Active Supplements"
          value={active.length}
          sphereColor={sphereColorAt(0)}
          valueSize="lg"
          labelVariant="section"
        />
        <GradientSphereMetricCard
          label="Paused"
          value={paused.length}
          sphereColor={sphereColorAt(1)}
          valueSize="lg"
          labelVariant="section"
        />
        <GradientSphereMetricCard
          label="Protocols Running"
          value={SUPPLEMENT_PROTOCOLS.length}
          sphereColor={sphereColorAt(2)}
          valueSize="lg"
          labelVariant="section"
        />
      </div>

      {/* ── Active Stack ───────────────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Active Stack</Subheading>
        <div className="mt-4 space-y-3">
          {active.map((supp) => (
            <SurfaceRow
              key={supp.id}
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-semibold ${TEXT_PRIMARY}`}>{supp.name}</span>
                  <Badge variant="zinc">{supp.brand}</Badge>
                  <Badge variant="zinc">{supp.protocol}</Badge>
                </div>
                <p className={`text-sm ${TEXT_SUBTLE}`}>
                  {supp.dosage} &middot; {supp.timing} &middot; Since {supp.startDate}
                </p>
                {supp.notes && (
                  <p className={`text-sm italic ${TEXT_FAINT}`}>{supp.notes}</p>
                )}
              </div>
              <Badge variant="zinc" className="shrink-0 whitespace-nowrap">Active</Badge>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>

      {/* ── Protocols ──────────────────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Protocols</Subheading>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SUPPLEMENT_PROTOCOLS.map((proto) => (
            <SurfaceRow key={proto.id} className="flex flex-col gap-2">
              <p className={`font-semibold ${TEXT_PRIMARY}`}>{proto.name}</p>
              <p className={`text-sm ${TEXT_SUBTLE}`}>{proto.goal}</p>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="zinc" size="wide" className={TEXT_SECONDARY}>Target: {proto.biomarkerTarget}</Badge>
              </div>
              <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
                {proto.supplements.map((sid) => (
                  <Badge key={sid} variant="zinc">{supplementNameById(sid)}</Badge>
                ))}
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>

      {/* ── Paused Supplements ─────────────────────────────── */}
      {paused.length > 0 && (
        <SurfaceCard as="article" className="opacity-60">
          <Subheading variant="dashboard">Paused</Subheading>
          <div className="mt-4 space-y-3">
            {paused.map((supp) => (
              <SurfaceRow
                key={supp.id}
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-semibold ${TEXT_PRIMARY}`}>{supp.name}</span>
                  <Badge variant="zinc">{supp.brand}</Badge>
                  <Badge variant="zinc">{supp.protocol}</Badge>
                </div>
                  <p className={`text-sm ${TEXT_SUBTLE}`}>
                    {supp.dosage} &middot; {supp.timing} &middot; Since {supp.startDate}
                  </p>
                  {supp.notes && (
                    <p className={`text-sm italic ${TEXT_FAINT}`}>{supp.notes}</p>
                  )}
                </div>
                <Badge variant="zinc" className="shrink-0 whitespace-nowrap">Paused</Badge>
              </SurfaceRow>
            ))}
          </div>
        </SurfaceCard>
      )}
    </div>
  )
}
